import { useCallback, useEffect, useRef, useState } from "react";
import type { UsersScope } from "@/components/admin/UsersTab";
import {
	createUser,
	listOrganizations,
	type CreateUserResult,
	type Organization,
	type UserPermission,
	type UserRole,
} from "@/lib/api";
import { useCurrentUserStore } from "@/stores/useCurrentUserStore";
import { TextInputField } from "@/components/common/TextInputField";
import { SelectInputField } from "@/components/common/SelectInputField";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { InviteLinkShare } from "@/components/admin/InviteLinkShare";
import { PermissionCheckboxes } from "@/components/admin/PermissionCheckboxes";
import { ALL_USER_PERMISSIONS } from "@/lib/permissions";

interface CreateUserDialogProps {
	scope: UsersScope;
	open: boolean;
	onClose: () => void;
	onCreated: () => void;
}

export function CreateUserDialog({ scope, open, onClose, onCreated }: CreateUserDialogProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);
	const currentRole = useCurrentUserStore((s) => s.role);
	const organizationId = useCurrentUserStore((s) => s.organizationId);

	const isSuperAdmin = currentRole === "SuperAdmin";
	// 他の組織への招待は管理ページに限る。組織画面では自組織に固定し、組織の欄を出さない。
	const choosesOrganization = isSuperAdmin && scope === "all";

	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	// 超管理者は招待先を取り違えないよう毎回選ぶ。
	const defaultOrgId = choosesOrganization ? "" : organizationId;
	const [selectedOrgId, setSelectedOrgId] = useState(defaultOrgId);
	const [role, setRole] = useState<UserRole>("Member");
	const [permissions, setPermissions] = useState<UserPermission[]>([]);
	const [orgs, setOrgs] = useState<Organization[]>([]);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const [submitError, setSubmitError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [created, setCreated] = useState<CreateUserResult | null>(null);

	useEffect(() => {
		const el = dialogRef.current;
		if (!el) return;
		if (open && !el.open) {
			el.showModal();
		} else if (!open && el.open) {
			el.close();
		}
	}, [open]);

	// Reset form and optionally fetch orgs when dialog opens
	useEffect(() => {
		if (open) {
			setFirstName("");
			setLastName("");
			setEmail("");
			setSelectedOrgId(defaultOrgId);
			setRole("Member");
			setPermissions([]);
			setOrgs([]);
			setErrors({});
			setSubmitError(null);
			setLoading(false);
			setCreated(null);

			if (isSuperAdmin) {
				// 失敗時はブラウザの未処理 rejection としてそのまま顕在化させる。
				// セレクタは空のまま残り、サブミット時の必須バリデーションで気づける。
				listOrganizations().then(setOrgs);
			}
		}
	}, [open, isSuperAdmin, defaultOrgId]);

	// 超管理者を付与できる組織はサーバー側の許可リストで決まる。許可外の組織を選んで
	// いる間は選択肢自体を出さず、選択済みだった場合は管理者に落とす。
	const superAdminAllowed = orgs.find((o) => o.id === selectedOrgId)?.superAdminAllowed ?? false;
	useEffect(() => {
		if (role === "SuperAdmin" && !superAdminAllowed) setRole("Admin");
	}, [role, superAdminAllowed]);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === "Escape") {
				if (!created) onClose();
				e.preventDefault();
			}
		},
		[onClose, created],
	);

	function validate(): boolean {
		const next: Record<string, string> = {};
		if (!firstName.trim()) next.firstName = "名は必須です。";
		if (!lastName.trim()) next.lastName = "姓は必須です。";
		if (!email.trim()) {
			next.email = "メールアドレスは必須です。";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			next.email = "有効なメールアドレスを入力してください。";
		}
		if (!selectedOrgId) next.organizationId = "組織を選択してください。";
		setErrors(next);
		return Object.keys(next).length === 0;
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!validate()) return;
		setSubmitError(null);
		setLoading(true);
		try {
			const result = await createUser({
				firstName,
				lastName,
				email,
				organizationId: selectedOrgId,
				role,
				permissions: role === "Member" ? permissions : [],
			});
			setCreated(result);
		} catch (err) {
			setSubmitError(err instanceof Error ? err.message : "予期しないエラーが発生しました。");
		} finally {
			setLoading(false);
		}
	}

	function handleClose() {
		onCreated();
		onClose();
	}

	if (!open) return null;

	return (
		<dialog
			ref={dialogRef}
			onKeyDown={handleKeyDown}
			style={{
				margin: "auto",
				padding: 0,
				border: "none",
				borderRadius: "var(--radius-4)",
				boxShadow: "var(--shadow-card)",
				background: "var(--surface-1)",
				width: 480,
			}}
			className="backdrop:bg-scrim"
		>
			{created ? (
				/* Invite link reveal view */
				<div>
					<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
						<h3
							style={{
								margin: 0,
								fontSize: "var(--text-title)",
								fontWeight: 600,
								color: "var(--fg-1)",
								fontFamily: "var(--font-sans)",
							}}
						>
							招待リンクを共有してください
						</h3>
					</div>
					<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
						<div
							style={{
								padding: "10px 14px",
								borderRadius: "var(--radius-3)",
								background: "var(--warning-subtle)",
								boxShadow: "0 0 0 1px var(--warning-line)",
								fontSize: "var(--text-ui)",
								color: "var(--warning)",
								fontFamily: "var(--font-sans)",
								lineHeight: "var(--leading-normal)",
							}}
						>
							このリンクは7日後に失効します。受け取った本人がパスワードを設定するまでログインできません。
						</div>
						<InviteLinkShare
							key={created.inviteId}
							userId={created.id}
							inviteUrl={created.inviteUrl}
						/>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							borderTop: "var(--pane-header-border)",
							padding: "12px 24px",
						}}
					>
						<ConfirmButton variant="primary" label="閉じる" loading={false} onClick={handleClose} />
					</div>
				</div>
			) : (
				/* Create user form */
				<form onSubmit={handleSubmit} noValidate>
					<div style={{ padding: "20px 24px 16px", borderBottom: "var(--pane-header-border)" }}>
						<h3
							style={{
								margin: 0,
								fontSize: "var(--text-title)",
								fontWeight: 600,
								color: "var(--fg-1)",
								fontFamily: "var(--font-sans)",
							}}
						>
							ユーザーを招待
						</h3>
					</div>
					<div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 16 }}>
						{submitError && (
							<div
								style={{
									padding: "10px 14px",
									borderRadius: "var(--radius-3)",
									background: "var(--danger-subtle)",
									boxShadow: "0 0 0 1px var(--danger-line)",
									fontSize: "var(--text-ui)",
									color: "var(--danger)",
									fontFamily: "var(--font-sans)",
								}}
							>
								{submitError}
							</div>
						)}
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<TextInputField
								label="姓"
								value={lastName}
								onChange={setLastName}
								placeholder="山田"
								error={errors.lastName}
							/>
							<TextInputField
								label="名"
								value={firstName}
								onChange={setFirstName}
								placeholder="太郎"
								error={errors.firstName}
							/>
						</div>
						<TextInputField
							label="メールアドレス"
							type="email"
							value={email}
							onChange={setEmail}
							placeholder="taro@example.com"
							autoComplete="off"
							error={errors.email}
						/>
						{choosesOrganization && (
							<SelectInputField
								label="組織"
								value={selectedOrgId}
								onChange={setSelectedOrgId}
								error={errors.organizationId}
								testId="create-user-organization"
							>
								<option value="" disabled>
									選択してください
								</option>
								{orgs
									.filter((o) => o.inviteAllowed)
									.map((o) => (
										<option key={o.id} value={o.id}>
											{o.name}
										</option>
									))}
							</SelectInputField>
						)}
						<SelectInputField
							label="ロール"
							value={role}
							onChange={(v) => setRole(v as UserRole)}
							disabled={currentRole !== "Admin" && currentRole !== "SuperAdmin"}
						>
							<option value="Member">メンバー</option>
							{(currentRole === "Admin" || currentRole === "SuperAdmin") && (
								<option value="Admin">管理者</option>
							)}
							{currentRole === "SuperAdmin" && superAdminAllowed && (
								<option value="SuperAdmin">超管理者</option>
							)}
						</SelectInputField>
						<PermissionCheckboxes
							value={role === "Member" ? permissions : ALL_USER_PERMISSIONS}
							disabled={loading || role !== "Member"}
							onChange={setPermissions}
						/>
					</div>
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 8,
							borderTop: "var(--pane-header-border)",
							padding: "12px 24px",
						}}
					>
						<ConfirmButton
							variant="ghost"
							label="キャンセル"
							loading={false}
							disabled={loading}
							onClick={onClose}
						/>
						<ConfirmButton
							type="submit"
							label="招待リンクを発行"
							loading={loading}
							data-testid="create-user-submit"
						/>
					</div>
				</form>
			)}
		</dialog>
	);
}
